import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import PricingPlans from './PricingPlans';
import AccountManager from './AccountManager';
import StripeService from '../../services/StripeService';
import './PaywallScreen.css';

const PaywallScreen = ({ user, onSubscriptionComplete }) => {
  const [currentStep, setCurrentStep] = useState('pricing'); // pricing, accounts, payment
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userAccounts, setUserAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePlanSelect = (planData) => {
    setSelectedPlan(planData);
    setCurrentStep('accounts');
  };

  const handleFreePlanContinue = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Set up free plan in Firebase
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        isSubscribed: true,
        subscriptionType: 'free',
        subscriptionStartDate: new Date(),
        maxAccounts: 1,
        maxVideosPerMonth: 10,
        videosUsedThisMonth: 0,
        currentPeriodStart: new Date(),
        twitterUsernames: [],
        updatedAt: new Date()
      });

      onSubscriptionComplete({
        type: 'free',
        maxAccounts: 1,
        maxVideosPerMonth: 10
      });
    } catch (error) {
      console.error('Error setting up free plan:', error);
      setError('Failed to set up free plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountsConfirm = (accounts) => {
    setUserAccounts(accounts);
    setCurrentStep('payment');
  };

  const handleAccountsSkip = () => {
    // User wants to add accounts later
    setUserAccounts([]);
    setCurrentStep('payment');
  };

  const handleStripeCheckout = async () => {
    if (!selectedPlan) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Start Stripe checkout process
      await StripeService.startCheckout(
        selectedPlan,
        user.uid,
        user.email
      );
    } catch (error) {
      console.error('Error starting checkout:', error);
      setError('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  // Check for successful payment on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    
    if (success === 'true') {
      // Verify subscription and complete the flow
      handlePaymentSuccess();
    }
  }, []);

  const handlePaymentSuccess = async () => {
    setLoading(true);
    try {
      // Verify the subscription with Stripe
      const subscriptionData = await StripeService.verifySubscription(user.uid);
      
      if (subscriptionData.isValid) {
        // Update local user accounts if they were selected
        if (userAccounts.length > 0) {
          const userRef = doc(db, 'users', user.uid);
          await updateDoc(userRef, {
            twitterUsernames: userAccounts,
            updatedAt: new Date()
          });
        }

        onSubscriptionComplete({
          type: subscriptionData.status,
          maxAccounts: subscriptionData.maxAccounts,
          maxVideosPerMonth: subscriptionData.maxVideosPerMonth,
          accounts: userAccounts
        });
      } else {
        setError('Subscription verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setError('Failed to verify payment. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPricing = () => {
    setCurrentStep('pricing');
    setSelectedPlan(null);
    setUserAccounts([]);
    setError('');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'pricing':
        return (
          <PricingPlans 
            onPlanSelect={handlePlanSelect}
            onFreePlanContinue={handleFreePlanContinue}
          />
        );
      
      case 'accounts':
        return (
          <AccountManager
            maxAccounts={selectedPlan?.accounts}
            isUnlimited={selectedPlan?.isUnlimited}
            selectedPlan={selectedPlan}
            onConfirm={handleAccountsConfirm}
            onSkip={handleAccountsSkip}
            onBack={handleBackToPricing}
          />
        );
      
      case 'payment':
        return (
          <div className="payment-step">
            <div className="payment-summary">
              <h2>Complete Your Purchase</h2>
              <div className="plan-summary">
                <h3>{selectedPlan?.isUnlimited ? 'Unlimited' : `${selectedPlan?.accounts} Account${selectedPlan?.accounts > 1 ? 's' : ''}`} Plan</h3>
                <div className="price-display">
                  <span className="amount">${selectedPlan?.price}</span>
                  <span className="period">/month</span>
                </div>
                {selectedPlan?.savings > 0 && (
                  <div className="savings">Save {selectedPlan?.savings}%</div>
                )}
              </div>
              
              {userAccounts.length > 0 && (
                <div className="selected-accounts">
                  <h4>Selected Accounts:</h4>
                  <ul>
                    {userAccounts.map((account, index) => (
                      <li key={index}>@{account}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="payment-actions">
                <button 
                  onClick={handleStripeCheckout}
                  className="checkout-button"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Proceed to Payment'}
                </button>
                <button 
                  onClick={() => setCurrentStep('accounts')}
                  className="back-button"
                  disabled={loading}
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="paywall-screen">
        <div className="paywall-loading">
          <div className="loading-animation">
            <div className="concentric-ring ring-1"></div>
            <div className="concentric-ring ring-2"></div>
            <div className="concentric-ring ring-3"></div>
            <div className="loading-center-dot"></div>
          </div>
          <h2>Setting up your subscription...</h2>
          <p>This will only take a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="paywall-screen">
      {error && (
        <div className="paywall-error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="error-close">×</button>
        </div>
      )}
      
      <div className="paywall-content">
        {renderStep()}
      </div>
      
      {currentStep !== 'pricing' && (
        <div className="paywall-progress">
          <div className="progress-steps">
            <div className={`progress-step ${currentStep === 'pricing' ? 'active' : 'completed'}`}>
              <span className="step-number">1</span>
              <span className="step-label">Choose Plan</span>
            </div>
            <div className="progress-line"></div>
            <div className={`progress-step ${currentStep === 'accounts' ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Add Accounts</span>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step">
              <span className="step-number">3</span>
              <span className="step-label">Payment</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaywallScreen; 